import { useState, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { useMediaUpload } from './useMediaUpload';

/**
 * Photo Upload Hook (Legacy - now uses R2 if configured)
 * 
 * This hook maintains backward compatibility while supporting R2 storage.
 * If R2 is configured, it uses R2. Otherwise, falls back to Supabase Storage.
 */
export function usePhotoUpload() {
  const { user } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  // Check if R2 is configured. NOTE: uploads go through the r2-upload edge
  // function (server-side), so we only need the PUBLIC config here — never the
  // secret keys, which must not ship in the browser bundle. See
  // docs/reviews/2026-07-04-security-audit.md.
  const useR2 = useMemo(() => !!(
    import.meta.env.VITE_R2_ACCOUNT_ID &&
    import.meta.env.VITE_R2_PUBLIC_URL
  ), []);
  
  // Always call useMediaUpload (hooks must be called unconditionally)
  const { uploadPhoto: uploadPhotoR2, uploadMultiplePhotos: uploadMultiplePhotosR2, uploading: uploadingR2, uploadProgress: uploadProgressR2 } = useMediaUpload();

  const uploadPhoto = async (file: File): Promise<string | null> => {
    if (!user) {
      toast.error('You must be logged in to upload photos');
      return null;
    }

    if (file.size > 10 * 1024 * 1024) { // 10MB limit
      toast.error('Photo must be smaller than 10MB');
      return null;
    }

    // Use R2 if configured
    if (useR2) {
      return await uploadPhotoR2(file, { folder: 'photos', compress: true });
    }

    // Fallback to Supabase Storage
    setUploading(true);
    setUploadProgress(0);

    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
      const filePath = `${user.id}/${fileName}`;

      const { data, error } = await supabase.storage
        .from('post-photos')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) throw error;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('post-photos')
        .getPublicUrl(filePath);

      setUploadProgress(100);
      return publicUrl;
    } catch (error) {
      console.error('Error uploading photo:', error);
      toast.error('Failed to upload photo');
      return null;
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const uploadMultiplePhotos = async (files: File[]): Promise<string[]> => {
    if (useR2) {
      return await uploadMultiplePhotosR2(files, { folder: 'photos', compress: true });
    }

    const uploadPromises = files.map(file => uploadPhoto(file));
    const results = await Promise.all(uploadPromises);
    return results.filter((url): url is string => url !== null);
  };

  return {
    uploadPhoto,
    uploadMultiplePhotos,
    uploading: useR2 ? uploadingR2 : uploading,
    uploadProgress: useR2 ? uploadProgressR2 : uploadProgress
  };
}
